import React, { useEffect, useState, useMemo, useCallback } from 'react'
import { connect } from 'react-redux'
import { withRouterProps } from '../../utils/with-router-props'

import { fetchApiDataIfNeeded, callApi } from '../../../actions/api'
import { apiResourceEndpoint, routes } from '../../../constants/routes'
import { createSelector } from '@reduxjs/toolkit'

import TemplateGroupTile from './template-group-tile'
import GroupQuestions from './group-questions'
import SHSForm from '../../utils/shs-form'

const selectInstallId = (state, installId) => installId
const selectInstallations = state => state.installations
const selectTemplateId = (state, templateId) => templateId
const selectTemplateGroups = state => state.templateGroups
const selectGroupQuestions = state => state.groupQuestions
const selectAnswers = state => state.answers
const makeInstallation = createSelector(
    [selectInstallations, selectInstallId],
    (installations, installId) => {
        return installations.find(installation => installation?.id === installId)
    }
)

const makeTemplateGroups = createSelector(
    [selectTemplateGroups, selectTemplateId],
    (templateGroups, templateId) => {
        return templateGroups.filter(group => group.templateId === templateId)
            .sort((a, b) => {
                if (a.displayOrder > b.displayOrder) {
                    return 1
                }
                return -1
            })
    }
)
const makeQuestionGroups = createSelector(
    [selectGroupQuestions, (state, tGroups) => tGroups],
    (groupQuestions, tGroups) => {
        const groupIds = tGroups.map(group => group.groupId)
        return groupQuestions.filter(question => groupIds.includes(question.groupId))
    }
)

const makeAnswers = createSelector(
    [selectAnswers, selectInstallId],
    (answers, installId) => {
        return answers.filter(answer => answer?.installationId === installId)
    }
)

const mapStateToProps = (state, ownProps) => {
    const installation = makeInstallation(state, Number(ownProps.params.installId))
    const answers = makeAnswers(state, Number(ownProps.params.installId))
    const templateGroups = makeTemplateGroups(state, installation?.templateId)
    const questionGroups = makeQuestionGroups(state, templateGroups)
    return {
        installation,
        templateGroups,
        questionGroups,
        answers
    }
}

const InstallationView = ({ dispatch, params, installation, templateGroups, questionGroups, answers, navigate }) => {
    const [activeGroupId, setActiveGroupId] = useState(null)
    const installId = params.installId
    const templateId = installation?.templateId
    const [width, setWidth] = useState(window.innerWidth)
    const [active, setActive] = useState('')
    const allGroupQuestionIds = useMemo(() => {
        return questionGroups.map(gq => gq.id)
    }, [questionGroups])

    const allAnswers = useMemo(() => {
        return answers.filter(answer => allGroupQuestionIds.includes(answer.groupQuestionId))
    }, [answers, allGroupQuestionIds])

    const allInstallGroupQuestionIds = useMemo(() => {
        return allGroupQuestionIds.filter(id => allAnswers.find(answer => answer.groupQuestionId === id))
    }, [allGroupQuestionIds, allAnswers])

    const allAnsweredQuestions = useMemo(() => {
        return answers.filter(answer =>
            allGroupQuestionIds.includes(answer.groupQuestionId) &&
            (answer._computed.statusValue === 'SUBMITTED' || answer._computed.statusValue === 'APPROVED')
        )
    }, [answers, allGroupQuestionIds])

    const allQuestionsAnswered = useMemo(() => {
        return allInstallGroupQuestionIds.length > 0 && allInstallGroupQuestionIds.length === allAnsweredQuestions.length
    }, [allInstallGroupQuestionIds, allAnsweredQuestions])

    const handleWindowSizeChange = () => {
        setWidth(window.innerWidth)
    }

    useEffect(() => {
        window.addEventListener('resize', handleWindowSizeChange)
        return () => {
            window.removeEventListener('resize', handleWindowSizeChange)
        }
    }, [])

    useEffect(() => {
        if (installId) {
            dispatch(fetchApiDataIfNeeded(apiResourceEndpoint('installations', 'VIEW', installId)))
        }
    }, [dispatch, installId])

    useEffect(() => {
        if (templateId) {
            dispatch(fetchApiDataIfNeeded(apiResourceEndpoint('template-groups', 'LIST'), { filter: [`template_id|${templateId}`] }))
        }
    }, [dispatch, templateId])

    const refreshInstallationData = useCallback(() => {
        if (installId) {
            dispatch(fetchApiDataIfNeeded(
                apiResourceEndpoint('installations', 'VIEW', installId),
                { _forceRefresh: true }
            ))
        }
    }, [dispatch, installId])

    if (!installation) {
        return <div>No installation found</div>
    }

    const handleActive = () => {
        if (width > 992 && active === '') {
            setActive('active')
        }
    }

    const renderTemplateGroups = () => {
        if (activeGroupId && width < 992) {
            return null
        }

        let allGroups = []
        templateGroups.forEach(group => {
            const gQuestions = questionGroups.filter(gq => gq.groupId === group.groupId)
            const groupQuestionIds = gQuestions.map(gq => gq.id)
            const groupAnswers = answers.filter(answer => groupQuestionIds.includes(answer.groupQuestionId))
            const installGroupQuestionIds = groupQuestionIds.filter(id => groupAnswers.find(answer => answer.groupQuestionId === id))
            const answeredRaw = groupAnswers.filter(answer => answer._computed.statusValue === 'SUBMITTED' || answer._computed.statusValue === 'APPROVED' || answer._computed.statusValue === 'REJECTED')
            const answered = [...new Map(answeredRaw.slice().sort((a, b) => a.id - b.id).map(a => [a.groupQuestionId, a])).values()]
            if (installGroupQuestionIds.length > 0) {
                allGroups.push(
                    <TemplateGroupTile key={group.id} group={group} questionIds={installGroupQuestionIds} answered={answered} onSetActiveGroupId={setActiveGroupId} activeGroupId={activeGroupId} />
                )
            }
        })
        return allGroups
    }

    const renderGroupQuestions = () => {
        if (!activeGroupId && width < 992) {
            return null
        } else if (!activeGroupId) {
            return (
                'Please select a topic for a list of questions'
            )
        }
        const activeGroup = templateGroups.find(group => group.groupId === activeGroupId)
        const gQuestions = questionGroups.filter(gq => gq.groupId === activeGroupId)
            .sort((a, b) => {
                if (a.displayOrder > b.displayOrder) {
                    return 1
                }
                return -1
            })

        const answerMatchesGroupQuestion = (answer, groupQuestion) => (
            answer.groupQuestionId === groupQuestion.id
            || (answer.groupQuestionId === null && answer.questionId === groupQuestion.questionId)
        )

        const activeGroupQuestions = gQuestions.filter(gq => answers.some(answer => answerMatchesGroupQuestion(answer, gq)))

        return (
            <GroupQuestions
                installation={installation}
                onSetActiveGroupId={setActiveGroupId}
                activeGroup={activeGroup}
                groupId={activeGroupId}
                groupQuestions={activeGroupQuestions}
                refreshInstallationData={refreshInstallationData} />
        )
    }

    const submitFX = (formData, formId) => {
        dispatch(callApi(apiResourceEndpoint('installations', 'UPDATE', installId, 'submit'), {}, formId))
    }

    const successFX = () => {
        navigate(routes.appIndex.path)
    }

    return (
        <>
            <div className="app-dashboard app-dash-install">
                <div className="header">
                    <div className="container">
                        <div className="header-details">
                            <h1>Installation for {installation.label}</h1>
                            <p>
                                VIN: {installation.vin}<br />
                                Template: {installation._computed.templateName}<br />
                            </p>
                        </div>
                        <div className="header-actions">
                            { installation?._computed?.statusDisplay === 'Submitted'
                                ? <div className="label-status">In Review</div>
                                : <SHSForm submitFX={submitFX} successFX={successFX} extraClass="install-submit-form"
                                    buttonDisabled={allQuestionsAnswered ? '' : 'disabled'} buttonLabel={allQuestionsAnswered
                                        ? 'Submit for review' : installation._computed.statusDisplay } />
                            }
                        </div>
                    </div>
                </div>
                <div className="installation-wrapper">
                    <div className="container">
                        <div className="dashboard-wrapper">
                            <div className="installation-render-wrapper">
                                <div className={`render-template-groups ${activeGroupId ? 'has-active' : ''} ${activeGroupId && width < 992 && 'mobile-hide'}`} onClick={handleActive}>
                                    {renderTemplateGroups()}
                                </div>
                                <div className={`render-group-questions ${activeGroupId ? 'active' : ''} ${width < 992 && 'mobile-show'}`} onClick={handleActive}>
                                    {renderGroupQuestions()}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

export default withRouterProps(connect(mapStateToProps)(InstallationView))
